import {
  Handshake,
  DesignServices,
  RequestQuote,
  SettingsSuggest,
  Code,
  Build,
  Backup,
  Security,
  EmojiEvents,
} from "@mui/icons-material";
import { IntlProvider } from "react-intl";
import { useContext, useEffect, useRef } from "react";
import { LangContext, iconSize } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";

const steps_pl = [
  {
    title: "Współpraca",
    icon: <Handshake />,
    description:
      "Rozmawiamy o potrzebach, celach biznesowych i problemach do rozwiązania.",
  },
  {
    title: "Projekt",
    icon: <DesignServices />,
    description:
      "Proponuję koncepcję rozwiązania i sposób działania aplikacji.",
  },
  {
    title: "Wycena",
    icon: <RequestQuote />,
    description:
      "Przedstawiam jasną wycenę i zakres prac – bez ukrytych kosztów.",
  },
  {
    title: "Dobór technologii",
    icon: <SettingsSuggest />,
    description:
      "Dobieram technologie dopasowane do skali projektu i przyszłego rozwoju.",
  },
  {
    title: "Wykonanie",
    icon: <Code />,
    description: "Tworzę aplikację etapami, z bieżącym podglądem postępów.",
  },
  {
    title: "Utrzymanie",
    icon: <Build />,
    description:
      "Zapewniam stabilne działanie aplikacji, aktualizacje i bieżące wsparcie.",
  },
  {
    title: "Backup danych",
    icon: <Backup />,
    description:
      "Konfiguruję regularne kopie zapasowe, aby dane były bezpieczne.",
  },
  {
    title: "Bezpieczeństwo",
    icon: <Security />,
    description:
      "Dbam o ochronę danych, autoryzację użytkowników i zabezpieczenia.",
  },
  {
    title: "Sukces",
    icon: <EmojiEvents />,
    description:
      "Gotowe rozwiązanie działa stabilnie, rozwija się i realnie wspiera biznes.",
    isSuccess: true,
  },
];

const steps_en = [
  {
    title: "Consultation",
    icon: <Handshake />,
    description:
      "We discuss your requirements, business goals, and the challenges to be solved.",
  },
  {
    title: "Solution Design",
    icon: <DesignServices />,
    description:
      "I propose a solution concept and define how the application will work.",
  },
  {
    title: "Project Estimate",
    icon: <RequestQuote />,
    description:
      "I provide a clear project estimate and scope of work — with no hidden costs.",
  },
  {
    title: "Technology Selection",
    icon: <SettingsSuggest />,
    description:
      "I choose technologies that fit the project's scale and support future growth.",
  },
  {
    title: "Development",
    icon: <Code />,
    description:
      "I build the application in stages, keeping you updated on progress throughout the process.",
  },
  {
    title: "Maintenance",
    icon: <Build />,
    description:
      "I ensure reliable operation, regular updates, and ongoing support.",
  },
  {
    title: "Data Backup",
    icon: <Backup />,
    description:
      "I configure regular backups to keep your data secure and recoverable.",
  },
  {
    title: "Security",
    icon: <Security />,
    description:
      "I implement data protection, user authentication, and security best practices.",
  },
  {
    title: "Success",
    icon: <EmojiEvents />,
    description:
      "The finished solution runs reliably, evolves with your needs, and delivers real business value.",
    isSuccess: true,
  },
];
export default function CooperationFlow() {
  const language = useContext(LangContext);
  const styles = {
    section: {
      /*backgroundColor: "#f9fafb",*/
      padding: "80px 16px",
      fontFamily: "sans-serif",
    },
    header: {
      maxWidth: "768px",
      margin: "0 auto 64px auto",
      textAlign: "center",
    },
    h2: {
      fontSize: "32px",
      fontWeight: "800",
      color: "#111827",
      marginBottom: "16px",
    },
    headerP: {
      fontSize: "18px",
      color: "#4b5563",
    },
    grid: {
      maxWidth: "1200px",
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "32px",
    },
    item: {
      position: "relative",
      backgroundColor: "#fff",
      padding: "32px", // p-8
      borderRadius: "16px",
      border: "1px solid #f3f4f6",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      transition: "transform 0.3s ease",
      display: "flex",
      flexDirection: "column",
    },
    iconWrapper: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "12px",
      borderRadius: "12px",
      marginBottom: "20px",
      border: "1px solid",
      color: "#ffffff",
      backgroundColor: "#1E3A5F",
      borderColor: "#e0e7ff",
    },
    h3: {
      fontSize: "20px",
      fontWeight: "700",
      color: "#ffffff",
      marginBottom: "12px",
      display: "inline-flex",
      alignItems: "center",
      gap: "12px",
    },
    descriptionP: {
      fontSize: "20px",
      color: "#4b5563",
      lineHeight: "1.6",
    },
  };

  return (
    <AnimatePresence>
      <motion.div
        style={styles.container}
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={textVariants}
      >
        <section style={styles.section}>
          <div style={styles.header}>
            {language === "pl" && (
              <>
                <h2>Jak wygląda współpraca?</h2>
                <p style={styles.headerP}>
                  Od analizy wymagań po gotowe wdrożenie – prowadzę projekt
                  przez cały proces tworzenia aplikacji, dbając o jakość,
                  bezpieczeństwo i cele biznesowe.
                </p>
              </>
            )}
            {language === "en" && (
              <>
                <h2>How does the collaboration work?</h2>
                <p style={styles.headerP}>
                  From requirements analysis to final deployment – I guide the
                  project through the entire application development process,
                  ensuring quality, security, and alignment with business goals.
                </p>
              </>
            )}
          </div>
          {language === "pl" && (
            <div style={styles.grid}>
              {steps_pl.map((step, index) => (
                <div key={index} style={styles.item}>
                  <div
                    style={{
                      ...styles.iconWrapper,
                      color: step.isSuccess
                        ? "#ffffff"
                        : styles.iconWrapper.color,
                      backgroundColor: step.isSuccess
                        ? "#059669"
                        : styles.iconWrapper.backgroundColor,
                    }}
                  >
                    <h3 style={styles.h3}>
                      {step.icon}
                      {"      "}
                      {step.title}
                    </h3>
                  </div>
                  <div>
                    <p style={styles.descriptionP}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {language === "en" && (
            <div style={styles.grid}>
              {steps_en.map((step, index) => (
                <div key={index} style={styles.item}>
                  <div
                    style={{
                      ...styles.iconWrapper,
                      color: step.isSuccess
                        ? "#ffffff"
                        : styles.iconWrapper.color,
                      backgroundColor: step.isSuccess
                        ? "#059669"
                        : styles.iconWrapper.backgroundColor,
                    }}
                  >
                    {step.icon}
                    <h3 style={styles.h3}>{step.title}</h3>
                  </div>
                  <div>
                    <p style={styles.descriptionP}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </motion.div>
    </AnimatePresence>
  );
}
